import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthSessionService } from './auth-session.service';
import { BUILD_INFO } from '../../generated/build-info';

const CSRF_RETRY = new HttpContextToken<boolean>(() => false);
const CSRF_HEADER = 'X-XSRF-TOKEN';
const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function buildAuthenticatedRequest(req: HttpRequest<unknown>, csrfToken: string | null): HttpRequest<unknown> {
  const headers: Record<string, string> = {
    'X-Client-Version': BUILD_INFO.clientVersion,
    'X-Client-Build': BUILD_INFO.buildId,
  };
  // Angular's built-in XSRF interceptor only handles same-origin/relative URLs.
  // The development environment uses an absolute API URL, so add the cookie
  // token explicitly for protected state-changing requests.
  if (STATE_CHANGING_METHODS.has(req.method) && !req.headers.has(CSRF_HEADER)) {
    if (csrfToken) headers[CSRF_HEADER] = csrfToken;
  }

  return req.clone({
    withCredentials: true,
    setHeaders: headers,
  });
}

export const authInterceptorFn: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authSessionService = inject(AuthSessionService);
  const isRefreshRequest = req.url.includes('/login/refresh-token');
  const authReq = buildAuthenticatedRequest(req, authSessionService.getCsrfToken());

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 426) {
        window.dispatchEvent(new CustomEvent('frontend-upgrade-required'));
        return throwError(() => error);
      }
      if (error.status === 403
        && error.error?.code === 'CSRF_TOKEN_INVALID'
        && !req.context.get(CSRF_RETRY)) {
        return from(authSessionService.ensureCsrfToken(true)).pipe(
          switchMap((csrfReady) => csrfReady
            ? next(buildAuthenticatedRequest(req, authSessionService.getCsrfToken())
              .clone({ context: req.context.set(CSRF_RETRY, true) }))
            : throwError(() => error)),
        );
      }
      if (error.status === 401 && authSessionService.isAuthenticated() && !isRefreshRequest) {
        return from(authSessionService.refreshToken()).pipe(
          switchMap((refreshed) => {
            if (!refreshed) {
              return throwError(() => error);
            }

            // The token is read again after a refresh so the retried request
            // never reuses the stale header from the failed request.
            return next(buildAuthenticatedRequest(req, authSessionService.getCsrfToken()));
          }),
          catchError(() => {
            authSessionService.expireSession();
            return throwError(() => error);
          }),
        );
      }

      if (error.status === 401 && !isRefreshRequest) {
        authSessionService.expireSession();
      }

      return throwError(() => error);
    }),
  );
};
